import { useState, memo, useCallback, useEffect, FocusEvent } from 'react';
import TextField, { TextFieldProps } from '@components/text-field/TextField';
import { checkIntegerInput, checkDecimalInput, isInteger, isDouble, isInRange } from '@components/text-field/utils';



interface NumberFieldProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
	value?: number | null,
	onChange?: (value: number | null) => void,
	valueUnits?: string,
	allowDecimal?: boolean,
	maxNumber?: number,
}


const NumberField = (props: NumberFieldProps) => {
	const { value, onChange, valueUnits, allowDecimal = false, onBlur, onFocus, maxNumber = 1000000000000000, ...otherProps } = props;
	
	const [ controlledValue, setControlledValue ] = useState<string>('');
	const [ focused, setFocused ] = useState(false);


	useEffect(() => {
		if (value === undefined) {
			return;
		}
		if (value === null && controlledValue !== '') {
			setControlledValue('');
		}
		if (value !== null && String(value).replace('.', '') !== String(controlledValue).replace(',', '')) {
			let isIntegerOrDouble = allowDecimal ? isDouble(value) : isInteger(value);
			if (!isIntegerOrDouble) return;

			let inRange = isInRange(value, maxNumber);
			if (!inRange) return;
			
			setControlledValue(String(value).replace('.', ','));
		}
	}, [ value ]);


	useEffect(() => {
		if (controlledValue && controlledValue.length > 0 && valueUnits && valueUnits.length > 0) {
			let newStringValue = focused
				? controlledValue.replace(` ${valueUnits}`, '')
				: `${controlledValue} ${valueUnits}`;
			setControlledValue(newStringValue);
		}
	}, [ focused ]);


	const handleOnChange = useCallback((value: string) => {
		if (value === undefined || value === null || value.length === 0) {
			setControlledValue('');
			if (onChange) onChange(null);
			return;
		}
		if (value === '0') {
			setControlledValue('0');
			if (onChange) onChange(0);
			return;
		}
		let trimmedValue = /^0[^.,]/.test(value)
			? value.replace(/^0/, '').trim()
			: /^[.,]/.test(value)
			? `0${value}`.trim()
			: value.trim();
		let isValid = allowDecimal ? checkDecimalInput(trimmedValue) : checkIntegerInput(trimmedValue);
		if (!isValid) return;
		
		let parsedValue = parseFloat(trimmedValue.replace(',', '.'));
		
		let isIntegerOrDouble = allowDecimal ? isDouble(parsedValue) : isInteger(parsedValue);
		if (!isIntegerOrDouble) return;

		let inRange = isInRange(parsedValue, maxNumber);
		if (!inRange) return;
		
		setControlledValue(trimmedValue.replace('.', ','));

		if (onChange) onChange(parsedValue);
	},[ allowDecimal, onChange ]);


	const handleOnBlur = useCallback((e: FocusEvent<HTMLInputElement, Element>) => {
		setFocused(false);
		if (onBlur) {
			onBlur(e);
		}
	},[ onBlur ]);


	const handleOnFocus = useCallback((e: FocusEvent<HTMLInputElement, Element>) => {
		setFocused(true);
		if (onFocus) {
			onFocus(e);
		}
	},[ onFocus ]);


	return (
		<TextField {...otherProps}
			value={controlledValue}
			onChange={handleOnChange}
			onBlur={handleOnBlur}
			onFocus={handleOnFocus} />
	)
}


export default memo(NumberField);